<script>
    // 납품처 기관 등록 + 그 기관의 운영자 초대. **소유자(사업자) 전용 화면**이다.
    //
    // ⚠️ 운영자 비밀번호를 여기서 입력받지 않는다. 운영자는 보통 다른 회사 사람이라,
    //    소유자가 비밀번호를 정해 알고 있는 상태 자체가 문제고 평문 전달도 따라온다.
    //    → 이메일만 등록하면 서버가 그 이메일로 **초대 메일**(수락 링크)을 보낸다(SERVER util/hubAdminInviteMail).
    //    비밀번호는 운영자가 링크에서 직접 정한다. 메일이 실패해도 초대는 남고, 화면에 링크를 띄워 직접 전달하게 한다.
    import { onMount } from "svelte";
    import { PRConnect } from "@projectroom/ui-lib";
    import { Section, Button, Badge, Input, Select } from "../../lib/ui";

    // 지금 편집 중인 허브. 운영자 화면(대시보드)은 **기관** 단위라, 허브 빌더 안에서 소유자의 기관 전체를
    // 섞어 보여 주면 다른 허브의 기관 [대시보드 열기]를 눌러 "이전 허브 관리자 화면이 나온다"가 된다
    // (실측 2026-10-19: fp7zkbh1bv 빌더에서 mrx8k9zz9u 의 기관 '그루브' 화면이 열림).
    // → 이 허브에 연결된 기관을 먼저 보여 주고, 다른 허브의 기관은 펼쳐야 보인다. 없으면(null) 예전처럼 전체.
    export let botId = null;

    let orgs = [];
    let admins = [];
    let hubBots = [];
    let selectedOrgId = "";
    let loading = true;
    let error = "";
    let copied = "";
    // 초대·재발급·재설정 결과 { email, kind, mailed, url } — 메일을 보냈는지 + 링크(메일이 안 오면 직접 전달).
    let notice = null;
    // 요청 중 — 두 번 눌러 메일이 두 통 가지 않게
    let sending = false;

    // 새 기관 — 이름만 받는다. 기관 코드는 서버가 채운다(HubSetup.createOrg 주석: 아무 화면에도 안 쓰이는 내부 핸들).
    let newOrgName = "";
    // 새 운영자
    let newAdminEmail = "";
    // ⚠️ 기본은 staff(대화 원문 못 봄). 초등학생 대화는 '관리에 꼭 필요한 사람'만 봐야 한다.
    let newAdminRole = "staff";
    let newAdminName = "";

    // 운영자 화면은 별도 호스트다. 수락/진입 시 심는 세션 쿠키가 **그 호스트에** 붙어야 하므로
    // 링크를 콘솔 도메인으로 만들면 안 된다(쿠키는 host-only).
    //
    // ⭐ 로컬에서도 서브도메인을 그대로 쓴다 — `hub-admin.localhost`. 런처가 `{슬러그}.localhost` 로
    //    도는 것과 같은 모양이라 진입 규칙이 하나로 통일되고, **운영과 로컬의 쿠키 경계도 같아진다**
    //    (경로 폴백은 콘솔과 호스트를 공유해서 쿠키가 한 칸에 섞였다).
    //    브라우저가 `*.localhost` 를 127.0.0.1 로 풀어 준다(크롬·엣지는 기본,
    //    안 푸는 브라우저는 /etc/hosts 에 한 줄). 서버 경로 폴백(`/hub-admin`)은 그대로 남아 있다.
    // ⚠️ IP 로 접속했을 때만 경로 폴백을 쓴다 — `hub-admin.127.0.0.1` 은 호스트명이 아니다.
    const hubAdminBase = () => {
        const { protocol, host, origin } = location;
        if (/^\d{1,3}(?:\.\d{1,3}){3}(?::|$)/.test(host)) return `${origin}/hub-admin`;
        return `${protocol}//hub-admin.${host.replace(/^hub-admin\./, "")}`;
    };
    const acceptLink = (token) => `${hubAdminBase()}/accept?t=${token}`;
    const enterLink = (token) => `${hubAdminBase()}/enter?t=${token}`;

    // ⚠️ 클릭 핸들러에서 바로 부를 것 — 서버 응답을 기다린 뒤 부르면 Safari 가 클립보드 쓰기를 막는다(조용히 실패).
    //    그래서 발급 직후 자동 복사는 하지 않고, 결과 상자의 [링크 복사]로 복사한다.
    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); copied = key; }
        catch (e) { copied = ""; }
    };

    // 서버가 만든 링크(메일에 실은 것과 같은 주소)가 우선. 서버가 호스트를 못 믿어 안 만들었으면 이 화면 주소로 만든다
    const showNotice = (result, email, kind) => {
        copied = "";
        notice = { email, kind, mailed: !!result?.mailed, url: result?.acceptUrl || acceptLink(result?.inviteToken) };
    };

    const loadOrgs = () => {
        PRConnect.send("/api/HubSetup/getOrgList", {}, (data) => {
            orgs = data.result || [];
            loading = false;
        });
    };
    const loadAdmins = () => {
        PRConnect.send("/api/HubSetup/getAdminList", { orgId: selectedOrgId || null },
            (data) => { admins = data.result || []; });
    };

    onMount(() => {
        loadOrgs();
        loadAdmins();
        // 기관에 연결할 허브 후보. 허브 타입 봇만 고를 수 있어야 한다.
        PRConnect.send("/api/Bot/getBotListById", {}, (data) => {
            hubBots = (data.result || []).filter(
                (b) => String(b.pageType || "").toUpperCase() === "HUB");
        });
    });

    // 허브 빌더에서 만든 기관은 이 허브에 바로 연결한다(botId) — 따로 고르게 두면 연결 안 된 기관이 생기고,
    // 그 기관은 가입 링크도 대시보드 허브도 없다.
    const createOrg = () => {
        error = "";
        PRConnect.send("/api/HubSetup/createOrg", { orgName: newOrgName, botId: botId || null },
            (data) => {
                newOrgName = "";
                if (botId && data.result?.id) selectedOrgId = data.result.id;
                loadOrgs();
            },
            (err) => { error = err.header?.message || "기관을 추가하지 못했습니다."; });
    };

    // 허브 연결은 소유자 결정이다 — "어느 납품처에 어떤 과정을 열어주는가"라서.
    const linkHub = (org, botId) => {
        error = "";
        PRConnect.send("/api/HubSetup/modifyOrg",
            { orgId: org.id, orgName: org.orgName, botId: botId || null },
            loadOrgs,
            (err) => { error = err.header?.message || "허브를 연결하지 못했습니다."; });
    };

    const removeOrg = (org) => {
        if (!confirm(`'${org.orgName}' 기관을 삭제할까요?`)) return;
        error = "";
        // 학습자가 남아 있으면 서버가 거절한다(계정과 진행도가 한꺼번에 사라지는 걸 막기 위해).
        PRConnect.send("/api/HubSetup/deleteOrg", { orgId: org.id }, loadOrgs,
            (err) => { error = err.header?.message || "삭제하지 못했습니다."; });
    };

    const inviteAdmin = () => {
        error = "";
        if (!selectedOrgId) { error = "기관을 먼저 선택해주세요."; return; }
        if (sending) return;
        sending = true;
        const email = newAdminEmail.trim();
        PRConnect.send("/api/HubSetup/inviteAdmin",
            { orgId: selectedOrgId, email, name: newAdminName || null, role: newAdminRole },
            (data) => {
                sending = false;
                newAdminEmail = ""; newAdminName = ""; newAdminRole = "staff";
                loadAdmins();
                showNotice(data.result, email, "invite");
            },
            (err) => { sending = false; error = err.header?.message || "초대하지 못했습니다."; });
    };

    // 재발급하면 이전 링크는 못 쓴다(토큰을 덮어쓴다)
    const reinvite = (a) => {
        if (sending) return;
        sending = true; error = "";
        PRConnect.send("/api/HubSetup/reinviteAdmin", { hubAdminId: a.id },
            (data) => { sending = false; loadAdmins(); showNotice(data.result, a.email, "invite"); },
            (err) => { sending = false; error = err.header?.message || "다시 보내지 못했습니다."; });
    };

    // 비밀번호 '재설정'도 값을 정해주는 게 아니라 초대 링크 재발급이다(메일로 간다).
    // ⚠️ 그 운영자의 기존 로그인이 전부 끊긴다(기기 분실 대응이기도 하다).
    const resetPassword = (a) => {
        if (sending) return;
        if (!confirm(`${a.email} 의 비밀번호를 재설정할까요?\n기존 로그인이 모두 해제되고, 새 비밀번호를 정하는 링크가 이 이메일로 갑니다.`)) return;
        sending = true; error = "";
        PRConnect.send("/api/HubSetup/resetAdminPassword", { hubAdminId: a.id },
            (data) => { sending = false; loadAdmins(); showNotice(data.result, a.email, "reset"); },
            (err) => { sending = false; error = err.header?.message || "재설정하지 못했습니다."; });
    };

    const removeAdmin = (a) => {
        if (!confirm(`${a.email} 운영자를 삭제할까요?`)) return;
        PRConnect.send("/api/HubSetup/deleteAdmin", { hubAdminId: a.id }, loadAdmins);
    };

    /**
     * 운영자 권한 변경. ⭐ manager 는 **학습자 대화 원문**을 볼 수 있다 —
     * 학습자가 만 14세 미만(초등학생)일 수 있어 아무에게나 줄 권한이 아니다.
     */
    const setRole = (a, role) => {
        if (role === "manager" && !confirm(
            `${a.email} 님에게 '관리자' 권한을 줍니다.\n\n` +
            `학습자의 대화 원문을 볼 수 있게 되고, 진행 초기화·계정 삭제도 할 수 있습니다.\n` +
            `학습자가 만 14세 미만이면 대화에 가족·학교생활 이야기가 그대로 들어 있습니다.\n` +
            `열람 기록은 모두 남습니다.`
        )) return;
        PRConnect.send("/api/HubSetup/setHubAdminRole", { hubAdminId: a.id, role },
            () => loadAdmins(),
            (err) => { error = err.header?.message || "권한을 바꾸지 못했습니다."; });
    };

    // 소유자가 그 기관 대시보드를 잠깐 들여다보는 예외 경로.
    // 쿠키가 host-only 라 콘솔에서 심을 수 없어, 60초짜리 교환 토큰을 받아 운영자 호스트로 넘긴다.
    // ⚠️ 이 경로의 세션은 role='manager' 다(소유자는 기관을 만든 당사자). 열람은 감사기록에
    //    ownerId 로 남는다 — HUB_ADMIN_ACCESS_LOG.
    const openDashboard = (org) => {
        PRConnect.send("/api/HubSetup/openDashboard", { orgId: org.id },
            (data) => window.open(enterLink(data.result.exchangeToken), "_blank"),
            (err) => { error = err.header?.message || "열지 못했습니다."; });
    };

    $: if (selectedOrgId !== undefined) loadAdmins();

    // ── 지금 허브 기준으로 가른다 ──
    $: thisOrgs = botId ? orgs.filter((o) => o.botId === botId) : orgs;
    $: otherOrgs = botId ? orgs.filter((o) => o.botId !== botId) : [];
    let showOther = false;
    $: shownOrgs = showOther ? [...thisOrgs, ...otherOrgs] : thisOrgs;
    // 운영자 섹션은 보이는 기관의 운영자만(다른 허브 운영자가 섞이면 누구 화면인지 헷갈린다)
    $: shownOrgIds = new Set(shownOrgs.map((o) => o.id));
    $: shownAdmins = botId ? admins.filter((a) => shownOrgIds.has(a.orgId)) : admins;
    // 운영자 초대는 기관을 골라야 한다 — 이 허브 기관이 하나면 그걸로 미리 골라 둔다
    $: if (botId && !selectedOrgId && thisOrgs.length === 1) selectedOrgId = thisOrgs[0].id;

    // 허브 고르기 라벨 — 새 허브는 다 "새 허브페이지"라 이름만으로는 못 가른다.
    //   지금 허브엔 "(이 허브)", 이름이 겹치는 허브엔 ID 끝자리를 붙인다.
    $: dupTitles = (() => {
        const seen = new Map();
        for (const b of hubBots) seen.set(b.title, (seen.get(b.title) || 0) + 1);
        return new Set([...seen].filter(([, n]) => n > 1).map(([t]) => t));
    })();
    const hubLabel = (b) =>
        `${b.title || "(이름 없음)"}${b.id === botId ? " (이 허브)" : dupTitles.has(b.title) ? ` · ${String(b.id).slice(-4)}` : ""}`;

    /**
     * ⚠️ **'관리자'가 한 명도 없으면 그 기관은 운영을 시작할 수 없다.**
     * 보호자 동의 기록(setCohortGuardianConsent)이 manager 전용이고, 그 기록이 없으면
     * 가입 QR 로 계정이 만들어지지 않는다 → 아무도 못 들어온다.
     * 그런데 권한 부여는 **이 콘솔(소유자)에서만** 가능하다. 납품처는 허브 어드민만 쓰므로
     * 여기서 알려주지 않으면 "QR 을 찍어도 가입이 안 된다"는 문의로 돌아온다.
     */
    $: acceptedAdmins = admins.filter((a) => !Number(a.pending));
    $: needsManager = selectedOrgId && acceptedAdmins.length > 0
        && !acceptedAdmins.some((a) => a.role === "manager");
</script>

<div class="tw-space-y-6">
    {#if error}<p class="tw-mb-0 tw-text-sm tw-text-destructive">{error}</p>{/if}

    <Section title="납품처 기관" description={botId
        ? "이 허브를 쓸 기관을 등록합니다. 여기서 추가한 기관은 이 허브에 바로 연결되고, 기관마다 운영자 화면(대시보드)이 따로 있습니다."
        : "이 허브를 쓸 기관을 등록하고, 기관마다 어떤 허브를 열어줄지 정합니다."}>
      <div class="tw-space-y-3">

        <form class="tw-flex tw-flex-wrap tw-items-end tw-gap-2" on:submit|preventDefault={createOrg}>
            <div>
                <label class="tw-mb-0 tw-block tw-text-xs tw-font-normal tw-text-muted-foreground" for="orgName">기관 이름</label>
                <input id="orgName" class="tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm"
                       placeholder="○○고등학교" bind:value={newOrgName} />
            </div>
            <Button type="submit">기관 추가</Button>
        </form>

        {#if loading}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">불러오는 중…</p>
        {:else if botId && thisOrgs.length === 0}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">
                이 허브에 연결된 기관이 없습니다. 위에서 기관을 추가하면 이 허브에 바로 연결되고,
                <b class="tw-text-foreground">[대시보드 열기]</b>로 그 기관의 운영자 화면을 볼 수 있습니다.
            </p>
        {:else if orgs.length === 0}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">등록된 기관이 없습니다.</p>
        {/if}
        {#if !loading && shownOrgs.length}
            <table class="tw-w-full tw-text-sm">
                <thead class="tw-text-left tw-text-muted-foreground">
                    <tr><th class="tw-py-2">기관</th><th>연결된 허브</th><th>기수</th><th>학습자</th><th></th></tr>
                </thead>
                <tbody>
                    {#each shownOrgs as o (o.id)}
                        <tr class="tw-border-t" class:tw-opacity-60={botId && o.botId !== botId}>
                            <td class="tw-py-2">
                                <div class="tw-font-medium">{o.orgName}</div>
                            </td>
                            <td>
                                <select class="tw-rounded tw-border tw-px-2 tw-py-1 tw-text-xs"
                                        value={o.botId || ""} on:change={(e) => linkHub(o, e.target.value)}>
                                    <option value="">연결 안 함</option>
                                    {#each hubBots as b (b.id)}<option value={b.id}>{hubLabel(b)}</option>{/each}
                                </select>
                            </td>
                            <td>{o.cohortCnt}</td>
                            <td>{o.learnerCnt}</td>
                            <td class="tw-space-x-1 tw-text-right">
                                <Button variant="outline" size="sm" on:click={() => (selectedOrgId = o.id)}>운영자</Button>
                                <Button variant="outline" size="sm" on:click={() => openDashboard(o)}>대시보드 열기</Button>
                                <Button variant="outline" size="sm" on:click={() => removeOrg(o)}>삭제</Button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
            <p class="tw-mb-0 tw-text-xs tw-text-muted-foreground">
                '대시보드 열기'는 소유자 자격으로 잠깐 들여다보는 예외 경로입니다(링크는 60초만 유효, 접근은 기록에 남습니다).
                같은 브라우저에서는 운영자 화면 로그인이 하나뿐이라, 다른 기관 대시보드를 열면 열려 있던 화면도 그 기관으로 바뀝니다.
            </p>
        {/if}
        {#if !loading && otherOrgs.length}
            <!-- 다른 허브의 기관 — 기본은 접어 둔다. 펼치면 같은 표에 흐리게 붙는다(연결 허브를 이 허브로 바꿔 데려올 수 있다). -->
            <button type="button" class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-xs tw-text-muted-foreground tw-underline"
                    on:click={() => (showOther = !showOther)}>
                {showOther ? "다른 허브의 기관 숨기기" : `다른 허브에 연결된 기관 ${otherOrgs.length}개 보기`}
            </button>
        {/if}
      </div>
    </Section>

    <Section title="운영자" description="기관 담당자에게 초대 메일을 보내 계정을 만들게 합니다. 비밀번호는 담당자가 직접 정합니다.">
      <div class="tw-space-y-3">
        <div class="tw-flex tw-items-center tw-gap-2">
            <select class="tw-rounded tw-border tw-px-2 tw-py-1 tw-text-sm" bind:value={selectedOrgId}>
                <option value="">{botId ? "이 허브의 전체 기관" : "전체 기관"}</option>
                {#each shownOrgs as o (o.id)}<option value={o.id}>{o.orgName}</option>{/each}
            </select>
        </div>

        <form class="tw-flex tw-flex-wrap tw-items-end tw-gap-2" on:submit|preventDefault={inviteAdmin}>
            <div>
                <label class="tw-mb-0 tw-block tw-text-xs tw-font-normal tw-text-muted-foreground" for="adminEmail">이메일</label>
                <input id="adminEmail" type="email" class="tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm"
                       placeholder="teacher@school.kr" bind:value={newAdminEmail} />
            </div>
            <div>
                <label class="tw-mb-0 tw-block tw-text-xs tw-font-normal tw-text-muted-foreground" for="adminName">이름(선택)</label>
                <input id="adminName" class="tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm"
                       placeholder="김선생" bind:value={newAdminName} />
            </div>
            <div>
                <label class="tw-mb-0 tw-block tw-text-xs tw-font-normal tw-text-muted-foreground" for="adminRole">권한</label>
                <select id="adminRole" class="tw-rounded-md tw-border tw-px-3 tw-py-1.5 tw-text-sm" bind:value={newAdminRole}>
                    <option value="staff">일반 — 명단·진도·통계</option>
                    <option value="manager">관리자 — 대화 원문까지</option>
                </select>
            </div>
            <Button type="submit" disabled={sending}>{sending ? "보내는 중…" : "초대 메일 보내기"}</Button>
        </form>
        <p class="tw-mb-0 tw-text-xs tw-text-muted-foreground">
            입력한 이메일로 초대 메일이 갑니다. 비밀번호는 운영자가 메일의 링크에서 직접 정합니다(링크는 14일 동안 유효).
            <br />
            <b class="tw-text-foreground">'관리자'</b>는 학습자 <b class="tw-text-foreground">대화 원문</b>까지 볼 수 있습니다.
            학습자가 만 14세 미만이면 꼭 필요한 담당자에게만 주세요. 열람 기록은 모두 남습니다.
        </p>

        <!-- 보낸 결과 + 링크. 메일이 실패했거나 안 왔을 때 직접 전달할 수 있게 링크를 늘 보여 준다 -->
        {#if notice}
            <div class="tw-rounded-lg tw-border tw-px-3 tw-py-2.5 tw-text-xs tw-leading-relaxed {notice.mailed
                ? 'tw-border-emerald-300 tw-bg-emerald-50 tw-text-emerald-900'
                : 'tw-border-amber-300 tw-bg-amber-50 tw-text-amber-900'}">
                {#if notice.mailed}
                    <b>{notice.email}</b> 로 {notice.kind === "reset" ? "비밀번호 재설정" : "초대"} 메일을 보냈습니다.
                    메일이 안 보이면 스팸함을 확인하거나, 아래 링크를 직접 전달해 주세요.
                {:else}
                    <b>{notice.email}</b> 로 메일을 보내지 못했습니다. 아래 링크를 복사해 직접 전달해 주세요.
                {/if}
                <div class="tw-mt-2 tw-flex tw-items-center tw-gap-2">
                    <input class="tw-min-w-0 tw-flex-1 tw-rounded-md tw-border tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-text-foreground"
                           readonly value={notice.url} on:focus={(e) => e.target.select()} aria-label="초대 링크" />
                    <Button variant="outline" size="sm" on:click={() => copy(notice.url, "notice")}>
                        {copied === "notice" ? "복사됨" : "링크 복사"}
                    </Button>
                    <Button variant="ghost" size="sm" on:click={() => (notice = null)}>닫기</Button>
                </div>
                <div class="tw-mt-1">링크를 연 사람이 비밀번호를 정합니다 — 그 담당자 본인에게만 전달하세요.</div>
            </div>
        {/if}

        {#if needsManager}
            <div class="tw-rounded-lg tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-3 tw-py-2.5 tw-text-xs tw-leading-relaxed tw-text-amber-900">
                <b>이 기관에 '관리자' 권한 운영자가 없습니다.</b><br />
                보호자 동의 기록이 '관리자' 전용이라, 지금 상태로는 담당자가
                <b>가입 QR 을 열 수 없습니다</b>(학습자가 가입되지 않습니다).
                주담당자 한 분의 권한을 <b>관리자</b>로 바꿔주세요.
            </div>
        {/if}

        {#if shownAdmins.length === 0}
            <p class="tw-mb-0 tw-text-sm tw-text-muted-foreground">등록된 운영자가 없습니다.</p>
        {:else}
            <table class="tw-w-full tw-text-sm">
                <thead class="tw-text-left tw-text-muted-foreground">
                    <tr><th class="tw-py-2">이메일</th><th>기관</th><th>권한</th><th>상태</th><th></th></tr>
                </thead>
                <tbody>
                    {#each shownAdmins as a (a.id)}
                        <tr class="tw-border-t">
                            <td class="tw-py-2">
                                <div class="tw-font-medium">{a.email}</div>
                                <div class="tw-text-xs tw-text-muted-foreground">{a.name || ""}</div>
                            </td>
                            <td>{a.orgName}</td>
                            <td>
                                <select class="tw-rounded-md tw-border tw-px-2 tw-py-1 tw-text-xs"
                                        value={a.role === "manager" ? "manager" : "staff"}
                                        on:change={(e) => setRole(a, e.target.value)}>
                                    <option value="staff">일반</option>
                                    <option value="manager">관리자</option>
                                </select>
                            </td>
                            <td>
                                {#if Number(a.pending)}
                                    <Badge variant="outline">초대 수락 전</Badge>
                                {:else}
                                    <span class="tw-text-xs tw-text-muted-foreground">
                                        {a.lastLoginAt ? String(a.lastLoginAt).slice(0, 10) : "로그인 기록 없음"}
                                    </span>
                                {/if}
                            </td>
                            <td class="tw-space-x-1 tw-text-right">
                                {#if Number(a.pending)}
                                    <Button variant="outline" size="sm" disabled={sending} on:click={() => reinvite(a)}>
                                        초대 다시 보내기
                                    </Button>
                                {:else}
                                    <Button variant="outline" size="sm" disabled={sending} on:click={() => resetPassword(a)}>
                                        비밀번호 재설정
                                    </Button>
                                {/if}
                                <Button variant="outline" size="sm" on:click={() => removeAdmin(a)}>삭제</Button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        {/if}
      </div>
    </Section>
</div>
